import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    if (response.data.requires_2fa) {
      return { requires2FA: true, email, mfaMethod: response.data.mfa_method || 'email' };
    }
    if (response.data.password_reset_required) {
      return { passwordResetRequired: true, email: response.data.email, message: response.data.message };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return { requires2FA: false, user: userData, mfaEnrollmentRequired: !!response.data.mfa_enrollment_required };
  };

  const completeForcedReset = async (email, currentPassword, newPassword) => {
    const response = await authAPI.forceChangePassword(email, currentPassword, newPassword);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verify2FA = async (email, otp) => {
    const response = await authAPI.verify2FA(email, otp);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = (options = {}) => {
    if (!options.silent && localStorage.getItem('token')) {
      authAPI.logout().catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  const isAdmin = () => hasRole('super_admin', 'special_admin', 'admin');
  const isSuperAdmin = () => hasRole('super_admin');
  const isSpecialAdmin = () => hasRole('special_admin');
  const isFinance = () => hasRole('finance', 'special_finance');
  const isSpecialFinance = () => hasRole('special_finance');
  const isSpecialInspection = () => hasRole('special_inspection');

  const value = {
    user,
    token,
    loading,
    login,
    verify2FA,
    completeForcedReset,
    logout,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isSpecialAdmin,
    isFinance,
    isSpecialFinance,
    isSpecialInspection,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
